export const heroData = {
  title: "Revolutionise Pool and Beach Services",
  subtitle: "SmartFlags",
  description: "Transform guest experience with intelligent crowd management. Instant service calls, seamless checkouts, and real-time monitoring for luxury resorts.",
  cta: "Request Demo",
  secondaryCta: "Watch Video",
  heroImage: "https://images.unsplash.com/photo-1729707690998-1d4c5d755c0f"
};

export const features = [
  {
    id: 1,
    icon: "Radio",
    title: "Smart Handheld Device",
    description: "Compact, waterproof devices designed like walkie-talkies. Two simple buttons for intuitive guest interaction.",
    image: "https://images.unsplash.com/photo-1617859047452-8510bcf207fd"
  },
  {
    id: 2,
    icon: "Bell",
    title: "Instant Waiter Call",
    description: "Guests press one button, staff receive immediate notification with exact seat location. No more flag waving.",
    image: "https://images.unsplash.com/photo-1722409195473-d322e99621e3"
  },
  {
    id: 3,
    icon: "CreditCard",
    title: "Quick Checkout",
    description: "Single-button checkout request alerts staff instantly. Streamline departures and reduce wait times.",
    image: "https://images.unsplash.com/photo-1584132967334-10e028bd69f7"
  },
  {
    id: 4,
    icon: "Monitor",
    title: "LED Status Screens",
    description: "Real-time order tracking displays. Guests see their order status, wait times, and service updates.",
    image: "https://images.unsplash.com/photo-1586500036706-41963de24d8b"
  },
  {
    id: 5,
    icon: "BarChart3",
    title: "Analytics Dashboard",
    description: "Track service efficiency, peak hours, response times, and guest satisfaction metrics.",
    image: "https://images.unsplash.com/photo-1728051104450-f7bc748a6fdd"
  },
  {
    id: 6,
    icon: "Zap",
    title: "Real-Time Alerts",
    description: "Staff receive push notifications on tablets/phones. Priority queuing for VIP guests.",
    image: "https://images.unsplash.com/photo-1759038085950-1234ca8f5fed"
  }
];

export const howItWorksSteps = [
  {
    step: 1,
    title: "Guest Receives Device",
    description: "Each poolside or beachside seat is equipped with a compact SmartFlags device upon guest arrival.",
    icon: "UserPlus"
  },
  {
    step: 2,
    title: "Service Request",
    description: "Guest presses the service button. Instant notification sent to staff dashboard with seat number.",
    icon: "Radio"
  },
  {
    step: 3,
    title: "Staff Response",
    description: "Waiter acknowledges request and heads to the specified location. Average response time: 2 minutes.",
    icon: "UserCheck"
  },
  {
    step: 4,
    title: "Checkout & Analytics",
    description: "Guest presses checkout button when ready. System tracks all interactions for performance insights.",
    icon: "TrendingUp"
  }
];

export const benefits = [
  {
    id: 1,
    title: "Enhanced Guest Satisfaction",
    description: "95% of guests report improved service experience. No more waving, waiting, or frustration.",
    icon: "Heart",
    stat: "95%",
    statLabel: "Satisfaction Rate"
  },
  {
    id: 2,
    title: "Increased Revenue",
    description: "Average 40% increase in F&B orders. Guests order more when service is effortless.",
    icon: "TrendingUp",
    stat: "40%",
    statLabel: "Revenue Boost"
  },
  {
    id: 3,
    title: "Operational Efficiency",
    description: "Reduce staff running time by 60%. Optimize team allocation based on real-time demand.",
    icon: "Gauge",
    stat: "60%",
    statLabel: "Time Saved"
  },
  {
    id: 4,
    title: "Data-Driven Insights",
    description: "Understand peak hours, popular items, and service bottlenecks. Make informed decisions.",
    icon: "LineChart",
    stat: "24/7",
    statLabel: "Monitoring"
  }
];

export const testimonials = [
  {
    id: 1,
    name: "Michael Chen",
    role: "General Manager",
    hotel: "Azure Bay Resort, Maldives",
    image: "https://images.pexels.com/photos/5371577/pexels-photo-5371577.jpeg",
    quote: "SmartFlags transformed our poolside service. Guest complaints dropped by 80% and our F&B revenue increased by 35% in just three months.",
    rating: 5
  },
  {
    id: 2,
    name: "Sofia Rodriguez",
    role: "Director of Operations",
    hotel: "Sunset Beach Club, Miami",
    image: "https://images.unsplash.com/photo-1759038085950-1234ca8f5fed",
    quote: "The analytics dashboard is a game-changer. We optimized our staff schedule and reduced wait times by half. Guests love the instant service.",
    rating: 5
  },
  {
    id: 3,
    name: "James Thompson",
    role: "Resort Owner",
    hotel: "Paradise Shores, Bahamas",
    image: "https://images.unsplash.com/photo-1728051104450-f7bc748a6fdd",
    quote: "Best investment we've made. The ROI was immediate. Guests specifically mention the service system in their 5-star reviews.",
    rating: 5
  }
];

export const stats = [
  { label: "Resorts Worldwide", value: "150+", icon: "Building2" },
  { label: "Guests Served Daily", value: "50K+", icon: "Users" },
  { label: "Avg Response Time", value: "2min", icon: "Clock" },
  { label: "Satisfaction Rate", value: "98%", icon: "Star" }
];

export const pricingPlans = [
  {
    id: 1,
    name: "Starter",
    price: "$299",
    period: "per month",
    description: "Perfect for small boutique hotels",
    features: [
      "Up to 25 devices",
      "Basic dashboard",
      "Email support",
      "Setup & training",
      "1 LED screen"
    ],
    popular: false
  },
  {
    id: 2,
    name: "Professional",
    price: "$699",
    period: "per month",
    description: "Ideal for mid-size resorts",
    features: [
      "Up to 75 devices",
      "Advanced analytics",
      "Priority support 24/7",
      "Custom branding",
      "3 LED screens",
      "Mobile staff app"
    ],
    popular: true
  },
  {
    id: 3,
    name: "Enterprise",
    price: "Custom",
    period: "contact us",
    description: "For large resort chains",
    features: [
      "Unlimited devices",
      "Full API access",
      "Dedicated account manager",
      "White-label solution",
      "Unlimited LED screens",
      "Integration support",
      "On-site training"
    ],
    popular: false
  }
];

export const faqs = [
  {
    id: 1,
    question: "How long does installation take?",
    answer: "Typical installation for a 50-device setup takes 2-3 days including staff training. We handle everything from device placement to system configuration."
  },
  {
    id: 2,
    question: "Are the devices waterproof?",
    answer: "Yes, all SmartFlags devices are IP67 rated - fully waterproof and sand-resistant. They're designed specifically for poolside and beach environments."
  },
  {
    id: 3,
    question: "What's included in staff training?",
    answer: "We provide comprehensive on-site training including dashboard usage, device maintenance, troubleshooting, and best practices. Training materials and 24/7 support included."
  },
  {
    id: 4,
    question: "Can we customize the system?",
    answer: "Absolutely! Professional and Enterprise plans include custom branding, configurable alerts, personalized LED displays, and integration with your existing POS systems."
  },
  {
    id: 5,
    question: "What about battery life?",
    answer: "Devices last 3-5 days on a single charge with typical use. We provide charging stations and recommend nightly charging routines."
  }
];